import type { ResidentApplicationData } from '@/types/application'

/**
 * Import-from-previous-application (employment) — React port of Angular's
 * `resident-application-add.component.ts` `importOldata()` / `setOldApplicationData()`
 * (employmentinfo branch).
 *
 * When an applicant starts a new application, the API may return `oldapplication`
 * with employment records from their last application. We offer to import them so the
 * applicant doesn't re-enter everything.
 */

/** Which list the imported employment records should populate. */
export type EmploymentImportMode = 'current' | 'previous'

/**
 * Whether to offer the "Import from Previous Application" employment prompt.
 *
 * Mirrors Angular `importOldata()` employmentinfo guard:
 *   selectedTab === 'employmentinfo' && applicationData && !showemppopup &&
 *   current_job && !current_job.length && !job_gap_description &&
 *   oldapplication && (oldapplication.current_employment.length || oldapplication.previous_employment.length)
 *
 * Additional React guards: edit-mode only (Angular self-serve flow is edit-mode) and
 * `oldapplication.user_data` present (Angular `setOldApplicationData` no-ops without it).
 */
export function shouldOfferEmploymentImport(
  applicationData: ResidentApplicationData | null | undefined,
  isReadOnly: boolean
): boolean {
  if (isReadOnly || !applicationData) return false

  const hasCurrentEmployment =
    (applicationData.current_employment?.length ?? 0) > 0 ||
    (applicationData.current_job?.length ?? 0) > 0
  if (hasCurrentEmployment) return false

  if (applicationData.job_gap_description) return false

  const oldApp = applicationData.oldapplication
  return (
    !!oldApp?.user_data &&
    ((oldApp.current_employment?.length ?? 0) > 0 ||
      (oldApp.previous_employment?.length ?? 0) > 0)
  )
}

/**
 * Build the locally-patched application data after the applicant chooses to import.
 * Returns `null` when there is nothing importable (no `oldapplication.user_data`),
 * matching Angular's `setOldApplicationData` early guard.
 *
 * - `mode: 'current'` (Angular `setOldApplicationData(true)`): old current employment
 *   becomes current employment; any old previous employment becomes previous employment.
 * - `mode: 'previous'` (Angular `setOldApplicationData('neutral')`): old current AND
 *   previous employment are all imported as previous employment.
 *
 * The patched object is dispatched into Redux so the existing employment load effects
 * re-map it into the form state (mirrors Angular re-running `setPrefillFormData`).
 */
export function buildEmploymentImportUpdate(
  applicationData: ResidentApplicationData,
  mode: EmploymentImportMode
): ResidentApplicationData | null {
  const oldApp = applicationData.oldapplication
  if (!oldApp?.user_data) return null

  const oldCurrent = oldApp.current_employment ?? []
  const oldPrevious = oldApp.previous_employment ?? []

  if (mode === 'current') {
    return {
      ...applicationData,
      employment_status: '1',
      current_employment: oldCurrent,
      ...(oldPrevious.length > 0
        ? { previous_employer: '1', previous_employment: oldPrevious }
        : {}),
    }
  }

  // mode === 'previous'
  return {
    ...applicationData,
    previous_employer: '1',
    previous_employment: [...oldCurrent, ...oldPrevious],
  }
}
